import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Flame, Sun, Sparkles, Wind, ArrowLeft, Calendar, Check, ChevronRight, Loader,
  MapPin, Clock, CalendarPlus, Package, Ticket, FileText,
} from 'lucide-react';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import IntakeForm from './IntakeForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIMARY = '#1f3d2b';

// ---- Small helpers ----
const isoDate = (d) => d.toISOString().slice(0, 10);
const formatLongDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
};

// ---- Reusable atoms ----
const Logo = () => (
  <img src="/logo.png" alt="Healthy Life Vitamins" className="h-12 mx-auto object-contain" />
);

export const PrimaryButton = ({ children, onClick, disabled, dataTestid }) => (
  <button
    data-testid={dataTestid}
    onClick={onClick}
    disabled={disabled}
    className="w-full rounded-2xl py-5 text-base sm:text-lg font-bold tracking-tight text-white shadow-[0_8px_30px_rgba(31,61,43,0.25)] active:scale-[0.98] transition-all disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed"
    style={{ backgroundColor: disabled ? undefined : PRIMARY, fontFamily: "'Manrope', sans-serif", minHeight: 64 }}
  >
    {children}
  </button>
);

export const StageWrap = ({ children, k }) => (
  <motion.div
    key={k}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
    className="flex-1 flex flex-col w-full max-w-md mx-auto"
  >
    {children}
  </motion.div>
);

export const BackHeader = ({ onBack, title }) => (
  <div className="flex items-center gap-3 mb-6 -mx-1">
    <button
      data-testid="booking-back-button"
      onClick={onBack}
      className="p-2 -ml-2 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
      aria-label="Back"
    >
      <ArrowLeft size={22} />
    </button>
    {title && (
      <h2 className="text-xl text-[#0d1f15] tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}>
        {title}
      </h2>
    )}
  </div>
);

// ---- Pack purchase stage ----
const PackPurchase = ({ pack, onBack, onError, onNeedsWaiver, defaults }) => {
  const isMembership = pack.id === 'monthly';
  const [firstName, setFirstName] = useState(defaults?.firstName || '');
  const [phone, setPhone] = useState(defaults?.phone || '');
  const [email, setEmail] = useState(defaults?.email || '');
  const [loading, setLoading] = useState(false);
  const canSubmit = firstName.trim() && phone.trim() && /^[^@]+@[^@]+\.[^@]+$/.test(email.trim());

  const doCheckout = async (customerInfo) => {
    setLoading(true);
    try {
      const endpoint = isMembership
        ? `${API}/bookings/membership/checkout`
        : `${API}/bookings/packs/checkout`;
      const payload = {
        first_name: customerInfo.firstName,
        phone: customerInfo.phone,
        email: customerInfo.email,
        origin_url: window.location.origin,
      };
      if (!isMembership) payload.pack_id = pack.id;
      const { data } = await axios.post(endpoint, payload);
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'waiver_required') {
        setLoading(false);
        onNeedsWaiver(customerInfo, () => doCheckout(customerInfo));
        return;
      }
      onError(detail || 'Could not start checkout.');
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!canSubmit) return;
    const customerInfo = { firstName: firstName.trim(), phone: phone.trim(), email: email.trim() };
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/waivers/check`, { params: { phone: customerInfo.phone, email: customerInfo.email } });
      if (!data.signed) {
        setLoading(false);
        onNeedsWaiver(customerInfo, () => doCheckout(customerInfo));
        return;
      }
    } catch {
      // Waiver check itself failed (network blip) — fail open, backend
      // still enforces the gate as a safety net via 'waiver_required'.
    }
    await doCheckout(customerInfo);
  };

  return (
    <StageWrap k={`pack-${pack.id}`}>
      <div className="px-4 pt-6 flex-1 flex flex-col">
        <BackHeader onBack={onBack} title={isMembership ? 'Wellness Membership' : 'Wellness Pack'} />

        <div className="bg-[#1f3d2b]/5 border border-[#1f3d2b]/10 rounded-3xl p-6 mb-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#d97736]/15 flex items-center justify-center mx-auto mb-4">
            <Package size={26} className="text-[#d97736]" strokeWidth={2} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#d97736] font-bold mb-2">
            {isMembership ? 'Best Value · Monthly' : 'Wellness Pack'}
          </div>
          <h2
            className="text-3xl text-[#0d1f15] mb-1 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {pack.sessions} Wellness Reset Sessions
          </h2>
          <p className="text-sm text-[#5a7062] mb-4">
            {isMembership
              ? 'Every month · credits refresh on renewal · 30-day expiry'
              : 'Use anytime within 30 days'}
          </p>
          <div className="flex items-baseline justify-center gap-3 flex-wrap">
            <span className="text-4xl text-[#0d1f15]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              ${Math.round(pack.price)}
              {isMembership && <span className="text-base text-[#5a7062] font-normal ml-1">/mo</span>}
            </span>
            <span className="text-sm text-[#5a7062] line-through">${pack.sessions * 69}</span>
            <span className="text-xs font-bold uppercase tracking-widest text-[#d97736]">
              Save ${Math.round(isMembership ? pack.savings_per_month : pack.savings)}
            </span>
          </div>
        </div>

        <div className="space-y-4 mb-4">
          {[
            { label: 'First name', value: firstName, setter: setFirstName, type: 'text', autoComplete: 'given-name', testid: 'pack-first-name', placeholder: 'Alex' },
            { label: 'Phone', value: phone, setter: setPhone, type: 'tel', autoComplete: 'tel', testid: 'pack-phone', placeholder: '(555) 555-5555', inputMode: 'tel' },
            { label: 'Email', value: email, setter: setEmail, type: 'email', autoComplete: 'email', testid: 'pack-email', placeholder: 'you@email.com', inputMode: 'email' },
          ].map((f) => (
            <div key={f.testid}>
              <label className="block text-[11px] font-bold uppercase tracking-[0.25em] text-[#5a7062] mb-2">
                {f.label}
              </label>
              <input
                data-testid={f.testid}
                type={f.type} value={f.value}
                onChange={(e) => f.setter(e.target.value)}
                placeholder={f.placeholder} autoComplete={f.autoComplete} inputMode={f.inputMode}
                className="w-full h-14 px-4 text-base bg-white border border-gray-300 rounded-2xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/15 transition"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              />
            </div>
          ))}
        </div>
        {isMembership && (
          <p className="text-[11px] text-[#5a7062] text-center px-4 leading-relaxed">
            Auto-renews at ${Math.round(pack.price)}/mo. Cancel anytime from your account.
          </p>
        )}
      </div>

      <div className="px-4 pb-8">
        <PrimaryButton dataTestid="pack-buy-button" disabled={!canSubmit || loading} onClick={handlePay}>
          {loading
            ? <span className="inline-flex items-center gap-2"><Loader className="animate-spin" size={18} /> Opening checkout…</span>
            : (isMembership
              ? `Start Membership · $${Math.round(pack.price)}/mo`
              : `Buy Pack · $${Math.round(pack.price)}`)}
        </PrimaryButton>
      </div>
    </StageWrap>
  );
};

// ---- 1. Home ----
const Home = ({ onStart, onPickPack, onSignWaiver, service }) => {
  const pack = service.packs?.[0];
  const membership = service.membership;
  return (
    <StageWrap k="home">
      {/* Header: logo + kicker */}
      <div className="pt-7 pb-1 flex flex-col items-center">
        <img src="/logo.png" alt="Healthy Life Vitamins" className="h-9 object-contain mb-3" />
        <span
          className="text-[10px] uppercase tracking-[0.4em] text-[#5a7062]"
          style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
        >
          Wellness Suite
        </span>
      </div>

      {/* Title */}
      <div className="px-5 mt-4 text-center">
        <h1
          className="text-[2.75rem] sm:text-5xl text-[#0d1f15] leading-[1] tracking-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontStyle: 'italic' }}
        >
          {service.name}
        </h1>
        <p
          className="text-sm text-[#5a7062] mt-2"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {service.description}
        </p>
      </div>

      {/* Feature chips */}
      <div className="px-4 mt-4">
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { Icon: Flame, label: 'Infrared' },
            { Icon: Sun, label: 'Red Light' },
            { Icon: Sparkles, label: 'Massage' },
            { Icon: Wind, label: 'Aromatherapy' },
          ].map(({ Icon, label }) => (
            <div
              key={label}
              className="flex flex-col items-center gap-1.5 rounded-2xl bg-white/60 backdrop-blur-sm border border-[#1f3d2b]/10 py-2.5 px-1"
            >
              <Icon size={16} strokeWidth={1.5} className="text-[#1f3d2b]" />
              <span
                className="text-[9px] sm:text-[10px] text-center text-[#3a4a40] leading-tight"
                style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing selection — 3 cards */}
      <div className="px-4 mt-5 space-y-2.5">
        <div
          className="text-[10px] uppercase tracking-[0.3em] text-[#5a7062] text-center"
          style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
        >
          Choose your plan
        </div>

        {/* Card 1 — Single */}
        <button
          data-testid="plan-single"
          onClick={onStart}
          className="w-full rounded-2xl bg-white border border-[#1f3d2b]/12 hover:border-[#1f3d2b]/45 active:scale-[0.99] transition-all px-4 py-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#1f3d2b]/10 flex items-center justify-center flex-shrink-0">
              <Clock size={16} className="text-[#1f3d2b]" />
            </div>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5a7062]"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Single Session
              </div>
              <div
                className="text-2xl text-[#0d1f15] leading-tight mt-0.5"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                ${Math.round(service.price)}
                <span className="text-xs text-[#5a7062] ml-1">/ session</span>
              </div>
              <div className="text-[11px] text-[#5a7062] mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                60 min · pay as you go
              </div>
            </div>
          </div>
          <ChevronRight size={18} className="text-[#1f3d2b] flex-shrink-0" />
        </button>

        {/* Card 2 — 2-Session Pack */}
        {pack && (
          <button
            data-testid={`plan-${pack.id}`}
            onClick={() => onPickPack(pack)}
            className="w-full rounded-2xl bg-white border border-[#1f3d2b]/12 hover:border-[#1f3d2b]/45 active:scale-[0.99] transition-all px-4 py-4 flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-[#d97736]/15 flex items-center justify-center flex-shrink-0">
                <Ticket size={16} className="text-[#d97736]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#d97736]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {pack.label} · Save ${Math.round(pack.savings)}
                </div>
                <div
                  className="text-2xl text-[#0d1f15] leading-tight mt-0.5"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  ${Math.round(pack.price)}
                  <span className="text-xs text-[#5a7062] ml-1">
                    · use in {service.pack_expiry_days || 30} days
                  </span>
                </div>
              </div>
            </div>
            <ChevronRight size={18} className="text-[#1f3d2b] flex-shrink-0" />
          </button>
        )}

        {/* Card 3 — Membership (highlighted) */}
        {membership && (
          <button
            data-testid="plan-membership"
            onClick={() => onPickPack(membership)}
            className="relative w-full rounded-2xl overflow-hidden active:scale-[0.99] transition-all pl-4 pr-4 pt-7 pb-4 flex items-center justify-between text-left text-white"
            style={{
              background: 'linear-gradient(135deg, #062d17 0%, #114d2a 100%)',
              boxShadow:
                '0 12px 32px rgba(6,45,23,0.35), inset 0 0 0 1.5px rgba(255,153,0,0.65)',
            }}
          >
            {/* Best-value ribbon (top-right corner) */}
            <div
              className="absolute top-2 right-3 text-[9px] uppercase tracking-[0.25em] font-black"
              style={{ fontFamily: "'Manrope', sans-serif", color: '#FF9900' }}
            >
              ★ Best Value
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'rgba(255,153,0,0.15)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,153,0,0.55)',
                }}
              >
                <Sparkles size={16} className="text-[#FF9900]" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF9900]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Membership
                </div>
                <div
                  className="text-2xl leading-tight mt-0.5"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  ${Math.round(membership.price)}
                  <span className="text-xs text-white/70 ml-1">/ month</span>
                </div>
                <div className="text-[11px] text-white/75 mt-0.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {membership.sessions} sessions/mo · save ${Math.round(membership.savings_per_month)}
                </div>
              </div>
            </div>
            <ChevronRight size={18} className="text-[#FF9900] flex-shrink-0 ml-2" />
          </button>
        )}
      </div>

      {/* Waiver / intake form button */}
      <div className="px-4 pt-2 pb-1 text-center">
        <button
          data-testid="home-sign-waiver"
          onClick={onSignWaiver}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#5a7062] hover:text-[#1f3d2b] transition-colors border border-[#1f3d2b]/15 hover:border-[#1f3d2b]/35 rounded-full px-4 py-2"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          <FileText size={12} strokeWidth={2} />
          Sign Intake Form
        </button>
      </div>

      {/* Sign-in link */}
      <div className="px-4 pt-3 pb-6 text-center">
        <a
          href="/account"
          data-testid="booking-account-link"
          className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5a7062] hover:text-[#1f3d2b] transition-colors"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Already a member? Sign in →
        </a>
      </div>
    </StageWrap>
  );
};

// ---- 2. Time selection ----
const TimeSelect = ({ onBack, onPick }) => {
  const today = isoDate(new Date());
  const tomorrow = isoDate(new Date(Date.now() + 86400000));
  const [selectedDate, setSelectedDate] = useState(today);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/bookings/availability`, { params: { date: selectedDate } })
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  return (
    <StageWrap k="time">
      <div className="px-4 pt-6 pb-2">
        <BackHeader onBack={onBack} title="Pick a time" />
        <div className="grid grid-cols-3 gap-2 mb-6">
          {[
            { id: today, label: 'Today' },
            { id: tomorrow, label: 'Tomorrow' },
          ].map(({ id, label }) => (
            <button
              key={id}
              data-testid={`booking-date-${label.toLowerCase()}`}
              onClick={() => setSelectedDate(id)}
              className={`rounded-xl py-3 text-sm font-semibold tracking-tight transition-colors ${
                selectedDate === id
                  ? 'bg-[#1f3d2b] text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {label}
            </button>
          ))}
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="booking-date-picker"
                className={`rounded-xl py-3 text-sm font-semibold tracking-tight text-center cursor-pointer transition-colors ${
                  ![today, tomorrow].includes(selectedDate)
                    ? 'bg-[#1f3d2b] text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                <Calendar className="inline mr-1" size={14} />
                <span>{![today, tomorrow].includes(selectedDate) ? formatLongDate(selectedDate).split(',')[1]?.trim() : 'Pick date'}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border border-gray-200 shadow-xl rounded-2xl overflow-hidden" align="center">
              <CalendarPicker
                mode="single"
                selected={selectedDate ? new Date(selectedDate + 'T12:00:00') : undefined}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(isoDate(date));
                    setCalendarOpen(false);
                  }
                }}
                disabled={(date) => date < new Date(today + 'T00:00:00')}
                initialFocus
                classNames={{
                  day_selected: 'bg-[#1f3d2b] text-white hover:bg-[#1f3d2b] hover:text-white focus:bg-[#1f3d2b] focus:text-white',
                  day_today: 'bg-[#1f3d2b]/10 text-[#1f3d2b] font-bold',
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <p className="text-xs text-[#5a7062] uppercase tracking-[0.25em] mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {formatLongDate(selectedDate)}
        </p>
      </div>

      <div className="flex-1 px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center text-gray-400 py-10 gap-2">
            <Loader className="animate-spin" size={16} /> Loading times…
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center text-gray-500 py-10 text-sm">No times for this date.</div>
        ) : (
          <div className="space-y-2.5">
            {slots.map(({ slot, label, available, reason }) => (
              <button
                key={slot}
                data-testid={`booking-slot-${slot}`}
                onClick={() => available && onPick(selectedDate, slot, label)}
                disabled={!available}
                className={`w-full rounded-2xl py-5 text-lg font-semibold tracking-tight border transition-colors text-left px-6 flex items-center justify-between ${
                  available
                    ? 'bg-white border-gray-200 text-[#0d1f15] hover:border-[#1f3d2b] active:bg-gray-50'
                    : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                }`}
                style={{ fontFamily: "'Manrope', sans-serif", minHeight: 64 }}
              >
                <span>{label}</span>
                {available ? (
                  <ChevronRight size={20} className="text-gray-400" />
                ) : (
                  <span className="text-[11px] uppercase tracking-widest text-gray-400">
                    {reason === 'taken' ? 'Booked' : 'Past'}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </StageWrap>
  );
};

// ---- 3. Customer info ----
const CustomerInfo = ({ onBack, onSubmit, defaults }) => {
  const [firstName, setFirstName] = useState(defaults?.firstName || '');
  const [phone, setPhone] = useState(defaults?.phone || '');
  const [email, setEmail] = useState(defaults?.email || '');
  const [pack, setPack] = useState(null);
  const [checking, setChecking] = useState(false);
  const canSubmit = firstName.trim() && phone.trim() && /^[^@]+@[^@]+\.[^@]+$/.test(email.trim());

  // Look up active pack as the user types phone (debounced).
  useEffect(() => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 7) { setPack(null); return; }
    let cancelled = false;
    setChecking(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/pack/by-phone/${digits}`);
        if (!cancelled) setPack(data.pack);
      } catch {
        if (!cancelled) setPack(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [phone]);

  return (
    <StageWrap k="info">
      <div className="px-4 pt-6 flex-1 flex flex-col">
        <BackHeader onBack={onBack} title="Your details" />

        <div className="space-y-4 mb-6">
          {[
            { label: 'First name', value: firstName, setter: setFirstName, type: 'text', autoComplete: 'given-name', testid: 'booking-first-name', placeholder: 'Alex' },
            { label: 'Phone', value: phone, setter: setPhone, type: 'tel', autoComplete: 'tel', testid: 'booking-phone', placeholder: '(555) 555-5555', inputMode: 'tel' },
            { label: 'Email', value: email, setter: setEmail, type: 'email', autoComplete: 'email', testid: 'booking-email', placeholder: 'you@email.com', inputMode: 'email' },
          ].map((f) => (
            <div key={f.testid}>
              <label className="block text-[11px] font-bold uppercase tracking-[0.25em] text-[#5a7062] mb-2">
                {f.label}
              </label>
              <input
                data-testid={f.testid}
                type={f.type}
                value={f.value}
                onChange={(e) => f.setter(e.target.value)}
                placeholder={f.placeholder}
                autoComplete={f.autoComplete}
                inputMode={f.inputMode}
                className="w-full h-14 px-4 text-base bg-white border border-gray-300 rounded-2xl focus:outline-none focus:border-[#1f3d2b] focus:ring-2 focus:ring-[#1f3d2b]/15 transition"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              />
            </div>
          ))}
        </div>

        {pack && (
          <div
            data-testid="pack-credit-banner"
            className="bg-[#1f3d2b]/5 border border-[#1f3d2b]/20 rounded-2xl p-4 mb-2 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[#d97736]/15 flex items-center justify-center flex-shrink-0">
              <Ticket size={18} className="text-[#d97736]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#0d1f15]">
                {pack.sessions_remaining} session credit{pack.sessions_remaining === 1 ? '' : 's'} available
              </div>
              <div className="text-xs text-[#5a7062]">
                Expires {new Date(pack.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 pb-8">
        <PrimaryButton
          dataTestid="booking-continue-button"
          disabled={!canSubmit}
          onClick={() => onSubmit({ firstName: firstName.trim(), phone: phone.trim(), email: email.trim(), pack })}
        >
          {checking ? 'Checking…' : pack ? 'Continue · Use Credit' : 'Continue to Payment'}
        </PrimaryButton>
      </div>
    </StageWrap>
  );
};

// ---- 4. Payment redirect ----
const PaymentRedirect = ({ booking, customer, service, onBack, onError, onNeedsWaiver }) => {
  const [loading, setLoading] = useState(false);

  const doCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings/checkout`, {
        first_name: customer.firstName,
        phone: customer.phone,
        email: customer.email,
        booking_date: booking.date,
        slot: booking.slot,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'waiver_required') {
        setLoading(false);
        onNeedsWaiver(customer, doCheckout);
        return;
      }
      onError(detail || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handlePay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/waivers/check`, { params: { phone: customer.phone, email: customer.email } });
      if (!data.signed) {
        setLoading(false);
        onNeedsWaiver(customer, doCheckout);
        return;
      }
    } catch {
      // fail open — backend still enforces via 'waiver_required'
    }
    await doCheckout();
  };

  return (
    <StageWrap k="payment">
      <div className="px-4 pt-6 flex-1 flex flex-col">
        <BackHeader onBack={onBack} title="Review & pay" />

        <div className="bg-[#1f3d2b]/5 border border-[#1f3d2b]/10 rounded-3xl p-6 mb-6">
          <div className="flex justify-between items-baseline mb-1">
            <h3
              className="text-xl text-[#0d1f15] tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
            >
              {service.name}
            </h3>
            <span
              className="text-2xl text-[#0d1f15]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              ${Math.round(service.price)}
            </span>
          </div>
          <p className="text-sm text-[#5a7062] mb-4">{service.description}</p>

          <div className="border-t border-[#1f3d2b]/10 pt-4 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-[#0d1f15]">
              <Calendar size={14} className="text-[#1f3d2b]" />
              {formatLongDate(booking.date)}
            </div>
            <div className="flex items-center gap-2 text-[#0d1f15]">
              <Clock size={14} className="text-[#1f3d2b]" />
              {booking.label}
            </div>
          </div>
        </div>

        <div className="text-xs text-[#5a7062] mb-6 text-center px-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Pay securely with Apple Pay, Google Pay, or card on the next screen.
        </div>
      </div>

      <div className="px-4 pb-8">
        <PrimaryButton
          dataTestid="booking-pay-button"
          onClick={handlePay}
          disabled={loading}
        >
          {loading ? (
            <span className="inline-flex items-center gap-2"><Loader className="animate-spin" size={18} /> Opening checkout…</span>
          ) : (
            `Confirm & Pay $${Math.round(service.price)}`
          )}
        </PrimaryButton>
      </div>
    </StageWrap>
  );
};

// ---- 5. Confirmation (separate route) ----
export const BookingSuccess = () => {
  const [state, setState] = useState({ status: 'polling', attempts: 0 });
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState({ status: 'error', reason: 'Missing session id' });
      return;
    }

    let attempts = 0;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await axios.get(`${API}/bookings/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setBooking(data.booking);
          setState({ status: 'paid' });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'error', reason: 'Checkout expired — try again.' });
          return;
        }
      } catch (e) {
        // network blip — keep polling
      }
      attempts += 1;
      if (attempts >= 10) {
        setState({ status: 'error', reason: 'Payment status check timed out. If you were charged, please contact us.' });
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { stopped = true; };
  }, []);

  const goHome = () => { window.location.href = '/booking'; };

  // Build a calendar .ics download
  const downloadIcs = () => {
    if (!booking) return;
    const [hh, mm] = booking.slot.split(':');
    const start = new Date(`${booking.booking_date}T${booking.slot}:00`);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Healthy Life Vitamins//Booking//EN',
      'BEGIN:VEVENT',
      `UID:${booking.id}@healthylifevitamins`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:Wellness Reset · 60-Min Private Session`,
      `LOCATION:Healthy Life Vitamins`,
      `DESCRIPTION:Your Wellness Reset session is confirmed.`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'wellness-reset.ics'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex-1 flex flex-col w-full max-w-md mx-auto px-4 pt-10 pb-8">
        <Logo />
        <div className="mt-3 text-center">
          <a
            href="/"
            data-testid="booking-success-home-link"
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#5a7062] hover:text-[#1f3d2b] transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <ArrowLeft size={11} strokeWidth={2.5} /> montclairvitamins.com
          </a>
        </div>

        {state.status === 'polling' && (
          <div className="flex-1 flex flex-col items-center justify-center text-[#5a7062] gap-3">
            <Loader className="animate-spin" size={28} />
            <p className="text-sm">Confirming your booking…</p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <h2 className="text-2xl text-[#0d1f15]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Something went wrong
            </h2>
            <p className="text-sm text-[#5a7062]">{state.reason}</p>
            <PrimaryButton onClick={goHome} dataTestid="booking-retry">Back to booking</PrimaryButton>
          </div>
        )}

        {state.status === 'paid' && booking && (
          <div className="flex-1 flex flex-col items-center text-center pt-6">
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: `${PRIMARY}15`, color: PRIMARY }}
            >
              <Check size={36} strokeWidth={3} />
            </motion.div>
            <h1 className="text-4xl text-[#0d1f15] mb-2 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              You&apos;re booked
            </h1>
            <p className="text-sm text-[#5a7062] mb-8">A confirmation has been recorded for your session.</p>

            <div className="w-full bg-[#1f3d2b]/5 border border-[#1f3d2b]/10 rounded-3xl p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-3">
                <Calendar size={16} className="text-[#1f3d2b]" />
                <span className="text-[#0d1f15] font-semibold">{formatLongDate(booking.booking_date)}</span>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Clock size={16} className="text-[#1f3d2b]" />
                <span className="text-[#0d1f15] font-semibold">{booking.slot_label}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#1f3d2b]" />
                <span className="text-[#0d1f15] font-semibold">Healthy Life Vitamins</span>
              </div>
            </div>

            <button
              data-testid="booking-add-to-calendar"
              onClick={downloadIcs}
              className="w-full rounded-2xl py-4 text-base font-bold border border-[#1f3d2b] text-[#1f3d2b] hover:bg-[#1f3d2b]/5 transition-colors inline-flex items-center justify-center gap-2"
              style={{ minHeight: 56 }}
            >
              <CalendarPlus size={18} /> Add to Calendar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- App shell ----
const BookingApp = () => {
  const [stage, setStage] = useState('home'); // home → time → info → payment (or home → pack)
  const [service, setService] = useState({
    name: 'Wellness Reset', description: '60-Min Private Session', price: 69,
  });
  const [booking, setBooking] = useState(null); // { date, slot, label }
  const [customer, setCustomer] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [error, setError] = useState('');
  const [waiverReturnStage, setWaiverReturnStage] = useState('home');
  const pendingResumeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/bookings/service`)
      .then(({ data }) => setService(data))
      .catch(() => { /* defaults */ });
  }, []);

  // Any stage that needs a paid/credit booking can call this to gate on the
  // intake form: it remembers where to come back to and what to resume once
  // the customer signs.
  const requireWaiver = (customerInfo, resume, fromStage) => {
    setCustomer((c) => ({ ...c, ...customerInfo }));
    setWaiverReturnStage(fromStage);
    pendingResumeRef.current = resume;
    setStage('waiver');
  };

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{
        background: 'radial-gradient(120% 60% at 50% 0%, #fef4e8 0%, #f6f4ef 55%, #efeadf 100%)',
      }}
    >
      {/* Home link — always accessible from any booking stage */}
      <a
        href="/"
        data-testid="booking-home-link"
        className="absolute top-4 left-4 z-20 inline-flex items-center gap-1 transition-opacity hover:opacity-100 opacity-50"
        style={{ color: '#1f3d2b', fontFamily: "'Manrope', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}
      >
        <ArrowLeft size={12} strokeWidth={2.5} /> Home
      </a>
      {/* Ambient warm glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-40"
        style={{ background: 'radial-gradient(closest-side, rgba(217,119,54,0.35), rgba(217,119,54,0) 70%)' }}
      />
      <AnimatePresence mode="wait">
        {stage === 'home' && (
          <Home
            key="home"
            onStart={() => setStage('time')}
            onPickPack={(p) => { setSelectedPack(p); setStage('pack'); }}
            onSignWaiver={() => { setWaiverReturnStage('home'); setStage('waiver'); }}
            service={service}
          />
        )}
        {stage === 'pack' && selectedPack && (
          <PackPurchase
            key="pack"
            pack={selectedPack}
            defaults={customer}
            onBack={() => setStage('home')}
            onError={(msg) => setError(msg)}
            onNeedsWaiver={(customerInfo, resume) => requireWaiver(customerInfo, resume, 'pack')}
          />
        )}
        {stage === 'time' && (
          <TimeSelect
            key="time"
            onBack={() => setStage('home')}
            onPick={(date, slot, label) => {
              setBooking({ date, slot, label });
              setStage('info');
            }}
          />
        )}
        {stage === 'info' && (
          <CustomerInfo
            key="info"
            defaults={customer}
            onBack={() => setStage('time')}
            onSubmit={async (c) => {
              setCustomer(c);
              try {
                const { data } = await axios.get(`${API}/waivers/check`, { params: { phone: c.phone, email: c.email } });
                if (!data.signed) {
                  requireWaiver(c, () => setStage('payment'), 'info');
                  return;
                }
              } catch {
                // fail open — PaymentRedirect still enforces via 'waiver_required'
              }
              setStage('payment');
            }}
          />
        )}
        {stage === 'payment' && booking && customer && (
          <PaymentRedirect
            key="payment"
            service={service}
            booking={booking}
            customer={customer}
            onBack={() => setStage('info')}
            onError={(msg) => setError(msg)}
            onNeedsWaiver={(customerInfo, resume) => requireWaiver(customerInfo, resume, 'payment')}
          />
        )}
        {stage === 'waiver' && (
          <IntakeForm
            key="waiver"
            defaults={customer}
            onBack={() => setStage(waiverReturnStage)}
            onSuccess={async (customerInfo) => {
              setCustomer((c) => ({ ...c, ...customerInfo }));
              const resume = pendingResumeRef.current;
              pendingResumeRef.current = null;
              if (resume) {
                await resume();
              } else {
                setStage(waiverReturnStage);
              }
            }}
          />
        )}
      </AnimatePresence>

      {error && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm" data-testid="booking-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default BookingApp;
